// cadImportAdapter.ts — Transform cv-cad positional JSON export → cv-web PanelData
//
// cv-cad exports panels with flat positional keys (mainPanel_1_9, standardOpenings_2_3, ...)
// cv-web uses typed objects with named fields (mpvar.thickness, wdvar[n].data.width, ...)
//
// This adapter bridges the two formats:
//   1. Detects cv-cad envelope shape (panelCount + flat-key panels)
//   2. Maps recognized positional columns → semantic fields using cadFieldMap
//   3. Returns cv-web PanelData + warnings[] for unrecognized fields
//   4. Partial import is always allowed — warnings never block import

import type { PanelData, Slot } from '../types/panelvar';
import { defaultPanelData } from '../types/panelvar';
import { CAD_FIELD_MAP, CV_WEB_SLOT_COUNTS, resolvePrefix } from './cadFieldMap';
import type { FieldMapping } from './cadFieldMap';

// ── Types ──────────────────────────────────────────────────────────────────────

/** The shape of a cv-cad named export envelope. */
export interface CadExportEnvelope {
  cvVersion:  string;
  type:       string;
  exportedAt: string;
  panelCount: number;
  titleBlock: Record<string, unknown> | null;
  panels:     CadExportPanel[];
}

/** A single panel in the cv-cad flat-key format. */
export type CadExportPanel = Record<string, unknown>;

export interface CadImportResult {
  panels:   PanelData[];
  warnings: string[];
}

// ── Detection ──────────────────────────────────────────────────────────────────

/** Returns true if the parsed JSON looks like a cv-cad named export (not cv-web native). */
export function isCadExport(obj: Record<string, unknown>): boolean {
  // cv-cad has panelCount + panels[] with flat positional keys
  // cv-web .cvpanel has panel (singular object) with *var keys
  if (typeof obj['panelCount'] !== 'number') return false;
  if (!Array.isArray(obj['panels'])) return false;
  const panels = obj['panels'] as unknown[];
  if (panels.length === 0) return true;
  const first = panels[0];
  if (typeof first !== 'object' || first === null) return false;
  // cv-cad panels have flat keys like mainPanel_1_1 or mainPanel_rowCount
  const keys = Object.keys(first as Record<string, unknown>);
  return keys.some(k => /^[a-zA-Z]+_\d+_\d+$/.test(k) || k.endsWith('_rowCount'));
}

// ── Conversion ─────────────────────────────────────────────────────────────────

/** Convert an entire cv-cad export into cv-web PanelData[] with import warnings. */
export function importCadExport(raw: CadExportEnvelope): CadImportResult {
  const warnings: string[] = [];
  const panels: PanelData[] = [];

  for (let i = 0; i < raw.panels.length; i++) {
    const cadPanel = raw.panels[i];
    const { panel, warnings: pw } = convertCadPanel(cadPanel, i);
    panels.push(panel);
    warnings.push(...pw);
  }

  if (raw.titleBlock && typeof raw.titleBlock === 'object') {
    warnings.push('Title block data present in cv-cad export — not yet mapped to cv-web fields.');
  }

  return { panels, warnings };
}

// ── Per-panel conversion ───────────────────────────────────────────────────────

function convertCadPanel(
  cadPanel: CadExportPanel,
  panelIndex: number,
): { panel: PanelData; warnings: string[] } {
  const warnings: string[] = [];
  const panel = defaultPanelData(crypto.randomUUID());

  // Collect all keys grouped by section prefix
  const sectionKeys = groupKeysBySection(cadPanel);

  // Track which cv-cad sections were recognized
  const recognizedSections = new Set<string>();

  for (const [sectionPrefix, keys] of Object.entries(sectionKeys)) {
    // Skip metadata keys
    if (['dateCode', 'panelName', 'projectName', 'sourceDictionaryKey', 'version'].includes(sectionPrefix)) {
      continue;
    }

    const resolvedSection = resolvePrefix(sectionPrefix);
    const mapping = CAD_FIELD_MAP[resolvedSection];
    if (!mapping) {
      warnings.push(`Panel ${panelIndex + 1}: Unrecognized section "${sectionPrefix}" (${keys.length} fields) — skipped.`);
      continue;
    }

    recognizedSections.add(sectionPrefix);
    const rowCountKey = `${sectionPrefix}_rowCount`;
    const rowCount = typeof cadPanel[rowCountKey] === 'number' ? cadPanel[rowCountKey] as number : 0;

    if (mapping.kind === 'object') {
      // Single-row section (mpvar, chvar, tpvar, lbvar)
      const result = convertObjectSection(cadPanel, sectionPrefix, mapping.columns, panelIndex);
      if (result.mapped > 0) {
        Object.assign((panel as unknown as Record<string, unknown>)[mapping.varKey] as Record<string, unknown>, result.data);
      }
      if (result.unmapped.length > 0) {
        warnings.push(`Panel ${panelIndex + 1} ${mapping.varKey}: ${result.unmapped.length} unmapped column(s): ${result.unmapped.join(', ')}`);
      }
    } else {
      // Array/slot section
      const result = convertArraySection(cadPanel, sectionPrefix, mapping.columns, rowCount, panelIndex);

      // Check slot overflow
      const maxSlots = mapping.maxSlots ?? CV_WEB_SLOT_COUNTS[mapping.varKey] ?? 99;
      if (result.rows.length > maxSlots) {
        warnings.push(
          `Panel ${panelIndex + 1} ${mapping.varKey}: cv-cad has ${result.rows.length} rows but cv-web max is ${maxSlots}. ` +
          `Extra rows truncated — consider increasing cv-web slot count.`
        );
      }

      // Merge rows into the panel's slot array.
      // Some CAD_FIELD_MAP sections (e.g. revisions → rvvar) don't yet have
      // a matching field in defaultPanelData. Rather than crash on
      // `undefined.length`, initialize a fresh slot array on the panel so
      // the data survives round-trip via re-export and surfaces a warning.
      // CV-774 / CV-751 — see ImportCVDialog stage 'cad-import'.
      let currentSlots = (panel as unknown as Record<string, unknown>)[mapping.varKey] as
        | Slot<Record<string, unknown>>[]
        | undefined;
      if (!Array.isArray(currentSlots)) {
        currentSlots = [];
        (panel as unknown as Record<string, unknown>)[mapping.varKey] = currentSlots;
        warnings.push(
          `Panel ${panelIndex + 1}: section "${sectionPrefix}" (${mapping.varKey}) not ` +
          `yet modeled in cv-web — ${result.rows.length} row(s) preserved as opaque ` +
          `slots so re-export round-trips losslessly.`,
        );
      }
      const targetCount = Math.max(currentSlots.length, result.rows.length);

      // Grow array if needed (pad with defaults from current slots)
      while (currentSlots.length < targetCount) {
        const template = currentSlots.length > 0
          ? { enabled: false, data: { ...currentSlots[0].data } }
          : { enabled: false, data: {} };
        currentSlots.push(template as Slot<Record<string, unknown>>);
      }

      for (let r = 0; r < Math.min(result.rows.length, targetCount); r++) {
        const rowData = result.rows[r];
        if (rowData && Object.keys(rowData).length > 0) {
          currentSlots[r] = { enabled: true, data: { ...currentSlots[r].data, ...rowData } };
        }
      }

      (panel as unknown as Record<string, unknown>)[mapping.varKey] = currentSlots;

      if (result.unmappedCols.length > 0) {
        warnings.push(`Panel ${panelIndex + 1} ${mapping.varKey}: ${result.unmappedCols.length} unmapped column(s): ${result.unmappedCols.join(', ')}`);
      }
    }
  }

  // Set panel mark from metadata if available
  const panelName = cadPanel['panelName'];
  if (typeof panelName === 'string' && panelName) {
    panel.mpvar.panelMark = panelName;
  }

  return { panel, warnings };
}

// ── Section grouping ───────────────────────────────────────────────────────────

/** Group flat keys by their section prefix. e.g. mainPanel_1_1 → "mainPanel" */
function groupKeysBySection(cadPanel: CadExportPanel): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const key of Object.keys(cadPanel)) {
    const match = key.match(/^([a-zA-Z]+)(?:_|$)/);
    if (match) {
      const prefix = match[1];
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(key);
    }
  }
  return groups;
}

// ── Object section conversion (mpvar, chvar, tpvar, lbvar) ─────────────────

function convertObjectSection(
  cadPanel: CadExportPanel,
  prefix: string,
  columns: Record<number, FieldMapping>,
  _panelIndex: number,
): { data: Record<string, unknown>; mapped: number; unmapped: string[] } {
  const data: Record<string, unknown> = {};
  let mapped = 0;
  const unmapped: string[] = [];

  // Object sections use row 1: prefix_1_col
  for (const key of Object.keys(cadPanel)) {
    const match = key.match(new RegExp(`^${prefix}_(\\d+)_(\\d+)$`));
    if (!match) continue;
    const col = parseInt(match[2], 10);
    const mapping = columns[col];
    if (mapping) {
      data[mapping.field] = coerceValue(cadPanel[key], mapping.type);
      mapped++;
    } else {
      unmapped.push(`col${col}`);
    }
  }

  return { data, mapped, unmapped };
}

// ── Array section conversion (wdvar, drvar, etc.) ──────────────────────────

function convertArraySection(
  cadPanel: CadExportPanel,
  prefix: string,
  columns: Record<number, FieldMapping>,
  _rowCount: number,
  _panelIndex: number,
): { rows: Record<string, unknown>[]; unmappedCols: string[] } {
  // Discover all rows and their columns
  const rowData: Record<number, Record<number, unknown>> = {};
  const unmappedColSet = new Set<string>();

  for (const key of Object.keys(cadPanel)) {
    const match = key.match(new RegExp(`^${prefix}_(\\d+)_(\\d+)$`));
    if (!match) continue;
    const row = parseInt(match[1], 10);
    const col = parseInt(match[2], 10);
    if (!rowData[row]) rowData[row] = {};
    rowData[row][col] = cadPanel[key];

    if (!columns[col]) {
      unmappedColSet.add(`col${col}`);
    }
  }

  // Convert each row using column map
  const maxRow = Math.max(0, ...Object.keys(rowData).map(Number));
  const rows: Record<string, unknown>[] = [];

  for (let r = 1; r <= maxRow; r++) {
    const rawRow = rowData[r];
    if (!rawRow) {
      rows.push({});
      continue;
    }
    const converted: Record<string, unknown> = {};
    for (const [colStr, value] of Object.entries(rawRow)) {
      const col = parseInt(colStr, 10);
      const mapping = columns[col];
      if (mapping) {
        converted[mapping.field] = coerceValue(value, mapping.type);
      }
    }
    rows.push(converted);
  }

  return { rows, unmappedCols: Array.from(unmappedColSet) };
}

// ── Value coercion ─────────────────────────────────────────────────────────────

function coerceValue(value: unknown, type: FieldMapping['type']): unknown {
  if (value === null || value === undefined) {
    switch (type) {
      case 'number':  return 0;
      case 'string':  return '';
      case 'boolean': return false;
    }
  }
  switch (type) {
    case 'number': {
      const n = Number(value);
      return isNaN(n) ? 0 : n;
    }
    case 'string':
      return String(value);
    case 'boolean': {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
      return false;
    }
  }
}
